<template>
  <v-container fluid>
    <!-- Buscador de Dispositivo -->
    <v-card class="mb-4">
      <v-card-title>Buscar Dispositivo Médico</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="selectedDispositivo"
              :items="dispositivosDisponibles"
              item-title="descripcion"
              item-value="id"
              label="Buscar por Dispositivo"
              :loading="loadingDispositivos"
              clearable
              @update:model-value="buscarTrazabilidad"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <v-list-item-subtitle>
                    SKU: {{ item.raw.sku }} | Lote: {{ item.raw.lote }} | S/N: {{ item.raw.serial_number || 'N/A' }}
                  </v-list-item-subtitle>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="busquedaSKU"
              label="Buscar por SKU"
              prepend-inner-icon="mdi-barcode"
              clearable
              @keyup.enter="buscarPorSKU"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="busquedaLote"
              label="Buscar por Lote"
              prepend-inner-icon="mdi-format-list-numbered"
              clearable
              @keyup.enter="buscarPorLote"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Información del Dispositivo -->
    <v-card v-if="trazabilidadData" class="mb-4">
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-information" class="mr-2" color="primary"></v-icon>
        Información del Dispositivo
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-list>
              <v-list-item>
                <v-list-item-title>Producto</v-list-item-title>
                <v-list-item-subtitle>{{ trazabilidadData.product?.name || 'N/A' }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>SKU</v-list-item-title>
                <v-list-item-subtitle>{{ trazabilidadData.product?.sku || 'N/A' }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Lote</v-list-item-title>
                <v-list-item-subtitle>{{ trazabilidadData.inventory_item?.lot_number || 'N/A' }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="12" md="6">
            <v-list>
              <v-list-item>
                <v-list-item-title>Número de Serie</v-list-item-title>
                <v-list-item-subtitle>{{ trazabilidadData.inventory_item?.serial_number || 'N/A' }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Ubicación Actual</v-list-item-title>
                <v-list-item-subtitle>{{ ubicacionActual || 'N/A' }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Cantidad</v-list-item-title>
                <v-list-item-subtitle>{{ trazabilidadData.inventory_item?.quantity || 0 }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Timeline de Eventos -->
    <v-card v-if="trazabilidadData">
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-timeline-clock" class="mr-2" color="primary"></v-icon>
        Historial Completo de Trazabilidad
      </v-card-title>
      <v-card-text>
        <v-timeline v-if="eventosCombinados.length > 0" side="end" align="start">
          <v-timeline-item
            v-for="(evento, index) in eventosCombinados"
            :key="`evento-${index}`"
            :dot-color="getEventoColor(evento.tipo)"
            size="small"
          >
            <template v-slot:icon>
              <v-icon :icon="getEventoIcon(evento.tipo)" size="small"></v-icon>
            </template>
            <v-card>
              <v-card-title class="text-h6">
                {{ getEventoTitulo(evento) }}
              </v-card-title>
              <v-card-text>
                <div class="text-body-2 mb-2">
                  <strong>Fecha:</strong> {{ formatDate(evento.fecha) }}
                </div>
                <div v-if="evento.detalles" class="text-body-2 mb-2">
                  <strong>Detalles:</strong> {{ formatDetalles(evento.detalles) }}
                </div>
                <div v-if="evento.usuario" class="text-body-2 mb-2">
                  <strong>Usuario:</strong> {{ evento.usuario }}
                </div>
                <div v-if="evento.cantidad_movida || evento.cantidad_antes || evento.cantidad_despues" class="text-body-2 mb-2">
                  <strong>Cantidad:</strong>
                  <span v-if="evento.cantidad_antes !== undefined && evento.cantidad_antes !== null">
                    {{ evento.cantidad_antes }} → {{ evento.cantidad_despues }}
                  </span>
                  <span v-else-if="evento.cantidad_movida">
                    {{ evento.cantidad_movida }} unidades
                  </span>
                </div>
                <div v-if="evento.ubicacion" class="text-body-2 mb-2">
                  <strong>Ubicación:</strong> {{ evento.ubicacion }}
                </div>
                <div v-if="evento.ubicacion_origen || evento.ubicacion_destino" class="text-body-2 mb-2">
                  <v-chip size="small" color="info" class="mr-2">
                    {{ evento.ubicacion_origen || 'N/A' }}
                  </v-chip>
                  <v-icon size="small">mdi-arrow-right</v-icon>
                  <v-chip size="small" color="success" class="ml-2">
                    {{ evento.ubicacion_destino || 'N/A' }}
                  </v-chip>
                </div>
                <div v-if="evento.referencia" class="text-body-2 mb-2">
                  <strong>Referencia:</strong> {{ evento.referencia }}
                </div>
                <div v-if="evento.notas" class="text-body-2">
                  <strong>Notas:</strong> {{ evento.notas }}
                </div>
                <div v-if="evento.estado_anterior || evento.estado_nuevo" class="text-body-2 mt-2">
                  <v-chip size="small" color="grey" class="mr-2">
                    {{ evento.estado_anterior || 'Sin estado' }}
                  </v-chip>
                  <v-icon size="small">mdi-arrow-right</v-icon>
                  <v-chip size="small" color="primary" class="ml-2">
                    {{ evento.estado_nuevo }}
                  </v-chip>
                </div>
                <div v-if="evento.servicio_origen || evento.servicio_destino" class="text-body-2 mt-2">
                  <v-chip size="small" color="info" class="mr-2">
                    {{ evento.servicio_origen || 'N/A' }}
                  </v-chip>
                  <v-icon size="small">mdi-arrow-right</v-icon>
                  <v-chip size="small" color="success" class="ml-2">
                    {{ evento.servicio_destino || 'N/A' }}
                  </v-chip>
                </div>
                <div v-if="evento.paciente" class="text-body-2 mt-2">
                  <strong>Paciente:</strong> {{ evento.paciente }}
                </div>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
        <v-alert v-else type="info">
          No se encontraron eventos de trazabilidad para este dispositivo.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Mensaje inicial -->
    <v-card v-else>
      <v-card-text class="text-center py-8">
        <v-icon size="64" color="grey-lighten-1">mdi-magnify</v-icon>
        <div class="text-h6 mt-4 text-grey">Busque un dispositivo médico para ver su historial completo</div>
        <div class="text-caption text-grey">Puede buscar por dispositivo, SKU o lote</div>
      </v-card-text>
    </v-card>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/api'

export default {
  name: 'HistorialCompletoDM',
  data() {
    return {
      loading: false,
      loadingDispositivos: false,
      selectedDispositivo: null,
      busquedaSKU: '',
      busquedaLote: '',
      dispositivosDisponibles: [],
      trazabilidadData: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    eventosCombinados() {
      if (!this.trazabilidadData) return []
      
      const eventos = []
      
      // Agregar eventos de trazabilidad general (RECEPTION, TRANSFER, DISPATCH, etc.)
      if (this.trazabilidadData.eventos_trazabilidad_general) {
        this.trazabilidadData.eventos_trazabilidad_general.forEach(evento => {
          eventos.push({
            tipo: 'trazabilidad_general',
            fecha: evento.event_timestamp,
            detalles: evento.event_description,
            notas: evento.notes,
            usuario: evento.user?.username || 'N/A',
            tipo_evento: evento.event_type,
            cantidad_antes: evento.quantity_before,
            cantidad_despues: evento.quantity_after,
            cantidad_movida: evento.quantity_moved,
            ubicacion: evento.location?.description || evento.location?.code || 'N/A',
            ubicacion_origen: evento.source_location?.description || evento.source_location?.code,
            ubicacion_destino: evento.destination_location?.description || evento.destination_location?.code,
            referencia: evento.operation_reference,
            transaction_id: evento.transaction_id,
            order_id: evento.order_id
          })
        })
      }
      
      // Agregar eventos de trazabilidad específicos de DM
      if (this.trazabilidadData.eventos_trazabilidad) {
        this.trazabilidadData.eventos_trazabilidad.forEach(evento => {
          eventos.push({
            tipo: 'evento_trazabilidad',
            fecha: evento.fecha_evento,
            detalles: evento.detalles,
            notas: evento.notas,
            usuario: evento.usuario_responsable?.username || 'N/A',
            estado_anterior: evento.detalles?.estado_anterior,
            estado_nuevo: evento.detalles?.estado_nuevo,
            servicio_origen: evento.detalles?.servicio_origen,
            servicio_destino: evento.detalles?.servicio_destino
          })
        })
      }
      
      // Agregar transferencias
      if (this.trazabilidadData.transferencias) {
        this.trazabilidadData.transferencias.forEach(transfer => {
          eventos.push({
            tipo: 'transferencia',
            fecha: transfer.fecha_transferencia,
            detalles: `Transferencia ${transfer.estado}`,
            notas: transfer.notas,
            usuario: transfer.usuario_responsable?.username || 'N/A',
            servicio_origen: transfer.servicio_origen?.nombre,
            servicio_destino: transfer.servicio_destino?.nombre,
            codigo_qr: transfer.codigo_qr
          })
        })
      }
      
      // Agregar implantes
      if (this.trazabilidadData.implantes) {
        this.trazabilidadData.implantes.forEach(implante => {
          eventos.push({
            tipo: 'implante',
            fecha: implante.fecha_implantacion,
            detalles: 'Implante en paciente',
            notas: implante.notas,
            usuario: implante.usuario_responsable?.username || 'N/A',
            paciente: `${implante.paciente?.nombre || ''} ${implante.paciente?.apellido || ''}`.trim() || implante.paciente?.run || 'N/A',
            fecha_explante: implante.fecha_explante
          })
        })
      }
      
      // Agregar cambios de estado
      if (this.trazabilidadData.historial_estados) {
        this.trazabilidadData.historial_estados.forEach(estado => {
          eventos.push({
            tipo: 'cambio_estado',
            fecha: estado.fecha_cambio,
            detalles: estado.motivo,
            notas: estado.notas,
            usuario: estado.usuario_responsable?.username || 'N/A',
            estado_anterior: estado.estado_anterior,
            estado_nuevo: estado.estado_nuevo
          })
        })
      }
      
      // Ordenar por fecha
      return eventos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
    },
    ubicacionActual() {
      if (!this.trazabilidadData?.inventory_item?.location) return 'N/A'
      const loc = this.trazabilidadData.inventory_item.location
      const parts = []
      if (loc.warehouse?.name) parts.push(loc.warehouse.name)
      if (loc.zone?.name) parts.push(loc.zone.name)
      if (loc.description) parts.push(loc.description)
      else if (loc.code) parts.push(loc.code)
      return parts.join(' - ') || 'N/A'
    }
  },
  mounted() {
    this.fetchDispositivosDisponibles()
  },
  methods: {
    async fetchDispositivosDisponibles() {
      this.loadingDispositivos = true
      try {
        const response = await apiClient.get('/dm/dispositivos-dm')
        this.dispositivosDisponibles = response.data.map(item => ({
          id: item.id,
          descripcion: `${item.product_name} - ${item.sku}`,
          sku: item.sku,
          lote: item.lot_number,
          serial_number: item.serial_number
        }))
      } catch (error) {
        console.error('Error al cargar dispositivos:', error)
        this.showSnackbar('Error al cargar dispositivos', 'error')
      } finally {
        this.loadingDispositivos = false
      }
    },
    
    async buscarTrazabilidad() {
      if (!this.selectedDispositivo) {
        this.trazabilidadData = null
        return
      }
      
      await this.fetchTrazabilidad(this.selectedDispositivo)
    },
    
    async buscarPorSKU() {
      if (!this.busquedaSKU) return
      
      const dispositivo = this.dispositivosDisponibles.find(d => 
        d.sku.toLowerCase() === this.busquedaSKU.toLowerCase()
      )
      
      if (dispositivo) {
        this.selectedDispositivo = dispositivo.id
        await this.fetchTrazabilidad(dispositivo.id)
      } else {
        this.showSnackbar('Dispositivo no encontrado con ese SKU', 'warning')
      }
    },
    
    async buscarPorLote() {
      if (!this.busquedaLote) return
      
      const dispositivo = this.dispositivosDisponibles.find(d => 
        d.lote && d.lote.toLowerCase() === this.busquedaLote.toLowerCase()
      )
      
      if (dispositivo) {
        this.selectedDispositivo = dispositivo.id
        await this.fetchTrazabilidad(dispositivo.id)
      } else {
        this.showSnackbar('Dispositivo no encontrado con ese lote', 'warning')
      }
    },
    
    async fetchTrazabilidad(inventoryItemId) {
      this.loading = true
      try {
        const response = await apiClient.get(`/dm/trazabilidad/${inventoryItemId}`)
        if (response.data?.success) {
          this.trazabilidadData = response.data.data
          this.showSnackbar('Trazabilidad cargada exitosamente', 'success')
        } else {
          this.showSnackbar('Error al cargar trazabilidad', 'error')
        }
      } catch (error) {
        console.error('Error al cargar trazabilidad:', error)
        const errorMessage = error.response?.data?.message || 'Error al cargar trazabilidad'
        this.showSnackbar(errorMessage, 'error')
        this.trazabilidadData = null
      } finally {
        this.loading = false
      }
    },
    
    getEventoColor(tipo) {
      const colors = {
        'trazabilidad_general': 'indigo',
        'evento_trazabilidad': 'blue',
        'transferencia': 'orange',
        'implante': 'green',
        'cambio_estado': 'purple'
      }
      return colors[tipo] || 'grey'
    },
    
    getEventoIcon(tipo) {
      const icons = {
        'trazabilidad_general': 'mdi-package-variant',
        'evento_trazabilidad': 'mdi-calendar-clock',
        'transferencia': 'mdi-arrow-right-bold-circle',
        'implante': 'mdi-account-heart',
        'cambio_estado': 'mdi-state-machine'
      }
      return icons[tipo] || 'mdi-circle'
    },
    
    getEventoTitulo(evento) {
      if (evento.tipo === 'trazabilidad_general') {
        const titulos = {
          'RECEPTION': 'Ingreso',
          'TRANSFER': 'Movimiento Interno',
          'DISPATCH': 'Egreso',
          'STORAGE': 'Almacenamiento',
          'PICKING': 'Recolección',
          'ADJUSTMENT': 'Ajuste de Inventario',
          'RETURN': 'Devolución',
          'QUALITY_CHECK': 'Control de Calidad',
          'DAMAGE': 'Producto Dañado',
          'EXPIRY': 'Vencimiento'
        }
        return titulos[evento.tipo_evento] || `Evento: ${evento.tipo_evento}`
      }
      
      const titulos = {
        'evento_trazabilidad': 'Evento de Trazabilidad',
        'transferencia': 'Transferencia',
        'implante': 'Implante',
        'cambio_estado': 'Cambio de Estado'
      }
      return titulos[evento.tipo] || 'Evento'
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    formatDetalles(detalles) {
      if (!detalles) return 'N/A'
      if (typeof detalles === 'string') return detalles
      if (typeof detalles === 'object') {
        return JSON.stringify(detalles)
      }
      return String(detalles)
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>

<style scoped>
.v-timeline-item {
  min-height: 60px;
}
</style>
